import { Box, Stack, TablePagination } from "@mui/material";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { HeatQualityFlagRows } from "components/heatQualityIssues/HeatQualityFlagRows";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { memo, useCallback, useMemo, useState } from "react";
import { HeatQualityIssue } from "src/store/api/generatedApi";

dayjs.extend(utc);

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const TIME_FORMAT = "YYYY-MM-DD HH:mm";

export const LoadingHeatIssuesTable = memo(
  ({
    issues,
    onIssueClick,
    highlightedHeatId,
  }: {
    issues: readonly HeatQualityIssue[];
    onIssueClick?: (issue: HeatQualityIssue) => void;
    highlightedHeatId?: string | null;
  }) => {
    const { t } = useTenantTranslation();

    const columns = useMemo<GridColDef<HeatQualityIssue>[]>(
      () => [
        { field: "heat_id", headerName: t("heat"), flex: 1, minWidth: 120 },
        {
          field: "tap_timestamp",
          headerName: t("tapTime"),
          flex: 1,
          minWidth: 150,
          valueGetter: (_value, row) =>
            row.tap_timestamp != null
              ? dayjs.utc(row.tap_timestamp).format(TIME_FORMAT)
              : "",
        },
        {
          field: "loading_start_time",
          headerName: t("loadingStart"),
          flex: 1,
          minWidth: 150,
          valueGetter: (_value, row) =>
            row.loading_start_time != null
              ? dayjs.utc(row.loading_start_time).format(TIME_FORMAT)
              : "",
        },
        {
          field: "issues",
          headerName: t("issues"),
          flex: 3,
          minWidth: 260,
          sortable: false,
          renderCell: (params) => (
            <Box sx={{ py: 0.5 }}>
              <HeatQualityFlagRows flags={params.row.flags} />
            </Box>
          ),
        },
      ],
      [t]
    );

    const getRowId = useCallback((row: HeatQualityIssue) => row.heat_id, []);

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
      {
        page: 0,
        pageSize: 50,
      }
    );

    return (
      <Stack spacing={1}>
        <TablePagination
          component="div"
          count={issues.length}
          page={paginationModel.page}
          onPageChange={(_event, page) => {
            setPaginationModel((model) => ({ ...model, page }));
          }}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(event) => {
            setPaginationModel({
              page: 0,
              pageSize: Number(event.target.value),
            });
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          sx={{ alignSelf: "flex-end" }}
        />
        <Box
          sx={{
            "& .heat-issue-row-highlighted": { bgcolor: "action.selected" },
            ...(onIssueClick && {
              "& .MuiDataGrid-row": { cursor: "pointer" },
            }),
          }}
        >
          <HotkeyCompliantDataGrid<HeatQualityIssue>
            autoHeight
            hideFooter
            rows={issues}
            columns={columns}
            getRowId={getRowId}
            getRowHeight={() => "auto"}
            getRowClassName={(params) =>
              params.row.heat_id === highlightedHeatId
                ? "heat-issue-row-highlighted"
                : ""
            }
            onRowClick={(params) => {
              onIssueClick?.(params.row as HeatQualityIssue);
            }}
            disableColumnSelector
            disableRowSelectionOnClick
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            initialState={{
              sorting: {
                sortModel: [{ field: "loading_start_time", sort: "desc" }],
              },
            }}
          />
        </Box>
      </Stack>
    );
  }
);
